import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useLocalState, clearOldCache, CACHE_VERSION, CACHE_TTL } from '../hooks';

// Use environment variable, fallback to current origin for production
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Clear old cache on load
clearOldCache();

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Authentication state with localStorage sync
  const [isAuthenticated, setIsAuthenticated] = useLocalState("church.authenticated", false);
  const [adminUser, setAdminUser] = useLocalState("church.adminUser", null);

  // Sync auth state from localStorage on mount
  useEffect(() => {
    try {
      const authKey = `${CACHE_VERSION}.church.authenticated`;
      const userKey = `${CACHE_VERSION}.church.adminUser`;
      const authData = localStorage.getItem(authKey);
      const userData = localStorage.getItem(userKey);
      
      if (authData) {
        const { data, timestamp } = JSON.parse(authData);
        if (data === true && timestamp && Date.now() - timestamp < CACHE_TTL) {
          setIsAuthenticated(true);
        }
      }
      if (userData) {
        const { data, timestamp } = JSON.parse(userData);
        if (data && timestamp && Date.now() - timestamp < CACHE_TTL) {
          setAdminUser(data);
        }
      }
    } catch (e) {
      console.error("Error syncing auth state:", e);
    }
  }, []);

  // Church data
  const [church, setChurch] = useLocalState('church.meta', {
    name: "Celestial Church of Christ — Valley of Berachah (VOB) Parish",
    address: "62 Spital Street, Dartford DA1 2DT",
    verseOfDay: "The Lord is my shepherd; I shall not want. — Psalm 23:1",
    givingUrl: "https://example.com/ccc-vob-giving",
    serviceTimes: {
      sunday: "10:00",
      wedMorning: "09:00",
      wedEvening: "19:00",
      fridayEvening: "19:00",
      newMoon: "22:00"
    }
  });
  const [serviceSchedules, setServiceSchedules] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [announcements, setAnnouncements] = useLocalState('church.ann', []);
  const [flyers, setFlyers] = useState([]);
  const [members, setMembers] = useLocalState('members', []);
  const [attendance, setAttendance] = useLocalState('attendance', []);
  
  // Additional church data
  const [events, setEvents] = useLocalState("church.events", []);
  const [sermons, setSermons] = useLocalState("church.sermons", []);
  const [prayers, setPrayers] = useLocalState("church.prayers", []);
  const [teams, setTeams] = useLocalState("church.teams", []);
  const [rehearsals, setRehearsals] = useLocalState("choir.rehearsals", []);
  const [setlist, setSetlist] = useLocalState("choir.setlist", []);

  // UI state
  const [isDark, setIsDark] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState("home");

  // Member registration
  const [registrationData, setRegistrationData] = useState({ full_name: '', phone: '', email: '' });
  const [registrationError, setRegistrationError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [memberId, setMemberId] = useLocalState("attendance.member_id", null);

  // Login handler
  const login = useCallback(async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.success) {
        const userData = response.data.user;
        setIsAuthenticated(true);
        setAdminUser(userData);
        return { success: true, user: userData };
      }
      return { success: false, error: response.data.message || 'Login failed' };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Login failed. Please try again.' };
    }
  }, [setIsAuthenticated, setAdminUser]);

  // Logout handler
  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setAdminUser(null);
    // Clear localStorage
    localStorage.removeItem(`${CACHE_VERSION}.church.authenticated`);
    localStorage.removeItem(`${CACHE_VERSION}.church.adminUser`);
    setCurrentTab("home");
  }, [setIsAuthenticated, setAdminUser]);

  // Comprehensive data fetch
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Load church settings
      const churchRes = await axios.get(`${API}/church`);
      if (churchRes.data) {
        const churchData = churchRes.data;
        setChurch({
          name: churchData.name,
          address: churchData.address || "",
          verseOfDay: churchData.verse_of_day,
          givingUrl: churchData.giving_url,
          serviceTimes: churchData.service_times
        });
      }

      // Load announcements (only published ones from enhanced API)
      const annRes = await axios.get(`${API}/announcements/enhanced`, { params: { include_all: false } });
      const publishedAnn = annRes.data.filter(a => a.status === 'published');
      setAnnouncements(publishedAnn);

      // Load other data in parallel
      const [eventsRes, sermonsRes, prayersRes, teamsRes, attRes, rehRes, songsRes, schedulesRes, ministriesRes, flyersRes] = await Promise.all([
        axios.get(`${API}/events`).catch(() => ({ data: [] })),
        axios.get(`${API}/sermons`).catch(() => ({ data: [] })),
        axios.get(`${API}/prayers`).catch(() => ({ data: [] })),
        axios.get(`${API}/teams`).catch(() => ({ data: [] })),
        axios.get(`${API}/attendance`).catch(() => ({ data: [] })),
        axios.get(`${API}/choir/rehearsals`).catch(() => ({ data: [] })),
        axios.get(`${API}/choir/songs`).catch(() => ({ data: [] })),
        axios.get(`${API}/service-schedule`).catch(() => ({ data: [] })),
        axios.get(`${API}/ministries`).catch(() => ({ data: [] })),
        axios.get(`${API}/flyers`).catch(() => ({ data: [] }))
      ]);

      setEvents(eventsRes.data);
      setSermons(sermonsRes.data);
      setPrayers(prayersRes.data);
      setTeams(teamsRes.data);
      setAttendance(attRes.data);
      setRehearsals(rehRes.data);
      setSetlist(songsRes.data);
      setServiceSchedules(schedulesRes.data);
      setMinistries(ministriesRes.data);
      setFlyers(flyersRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, [setChurch, setAnnouncements, setEvents, setSermons, setPrayers, setTeams, setAttendance, setRehearsals, setSetlist]);

  // Refresh announcements
  const refreshAnnouncements = useCallback(async () => {
    try {
      const annRes = await axios.get(`${API}/announcements/enhanced`, { params: { include_all: false } });
      const publishedAnn = annRes.data.filter(a => a.status === 'published');
      setAnnouncements(publishedAnn);
    } catch (error) {
      console.error("Error refreshing announcements:", error);
    }
  }, [setAnnouncements]);

  // Handle member registration
  const handleRegistration = useCallback(async () => {
    if (!registrationData.full_name || !registrationData.phone) {
      setRegistrationError("Please fill in all required fields");
      return { success: false };
    }
    
    try {
      const response = await axios.post(`${API}/members/register`, {
        full_name: registrationData.full_name,
        phone: registrationData.phone,
        email: registrationData.email || ""
      });
      
      const newMemberId = response.data.member_id;
      setMemberId(newMemberId);
      setRegistrationError("");
      setRegistrationSuccess(true);
      setRegistrationData({ full_name: "", phone: "", email: "" });
      return { success: true, memberId: newMemberId };
    } catch (error) {
      console.error("Registration error:", error);
      setRegistrationError(error.response?.data?.detail || "Registration failed. Please try again.");
      return { success: false };
    }
  }, [registrationData, setMemberId]);

  // Initial data fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const value = {
    // Auth
    isAuthenticated,
    setIsAuthenticated,
    adminUser,
    setAdminUser,
    login,
    logout,
    
    // Church data
    church,
    setChurch,
    serviceSchedules,
    setServiceSchedules,
    ministries,
    setMinistries,
    announcements,
    setAnnouncements,
    flyers,
    setFlyers,
    members,
    setMembers,
    attendance,
    setAttendance,
    
    // Additional data
    events,
    setEvents,
    sermons,
    setSermons,
    prayers,
    setPrayers,
    teams,
    setTeams,
    rehearsals,
    setRehearsals,
    setlist,
    setSetlist,
    
    // UI
    isDark,
    setIsDark,
    loading,
    currentTab,
    setCurrentTab,
    
    // Registration
    registrationData,
    setRegistrationData,
    registrationError,
    setRegistrationError,
    registrationSuccess,
    setRegistrationSuccess,
    memberId,
    setMemberId,
    handleRegistration,
    
    // Actions
    fetchAllData,
    refreshAnnouncements,
    
    // Constants
    API,
    BACKEND_URL
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
